import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useChainId } from 'wagmi';
import { readContract } from '@wagmi/core';
import { config } from '@/config/index';
import { getMarketplaceContract } from '@/constants/marketplace/contracts';
import { offersAbi } from '@/constants/marketplace/abi/offers';
import { type Offer } from '@chewy/typescript-config/marketplace-types';
import { type Address } from 'viem';
import { useNFTCollection } from '@/hooks/portfolio/useNFTCollection';
import { type ChainType } from '@/constants/chain-specific';
export interface NFTWithOffers {
  tokenId: string;
  offers: Offer[];
  metadata?: {
    name?: string;
    image?: string;
  };
}
interface CollectionOffersResult {
  nftsWithOffers: NFTWithOffers[];
  loading: boolean;
  error: Error | null;
  totalOffersCount: number;
  refetch: () => void;
}

/**
 * Hook to get all NFTs with offers for a specific collection
 * Efficiently filters marketplace offers by collection contract address
 */
export function useCollectionOffers(contractAddress: string, collectionName: string, userAddress: Address, chainId: ChainType, enabled = true): CollectionOffersResult {
  const [nftsWithOffers, setNftsWithOffers] = useState<NFTWithOffers[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const lastFetchKeyRef = useRef<string>('');
  const marketplaceContract = getMarketplaceContract(chainId);
  const marketplaceAddress = marketplaceContract?.address;

  // Get NFT collection data to map tokenIds to metadata
  const {
    tokens: collectionTokens,
    isLoading: tokensLoading
  } = useNFTCollection(contractAddress as Address, collectionName, chainId, userAddress);

  // Create a map of tokenId to metadata for quick lookup - stabilized
  const tokenMetadataMap = useMemo(() => {
    const map = new Map<string, {
      name?: string;
      image?: string;
    }>();
    collectionTokens.forEach(token => {
      map.set(token.tokenId.toString(), {
        name: token.metadata?.name,
        image: token.metadata?.image
      });
    });
    return map;
  }, [collectionTokens]);

  // Store latest values in refs to avoid dependency issues
  const latestValuesRef = useRef({
    marketplaceAddress,
    chainId,
    contractAddress,
    collectionName,
    enabled,
    tokenMetadataMap,
    collectionTokens
  });

  // Update ref when values change
  useEffect(() => {
    latestValuesRef.current = {
      marketplaceAddress,
      chainId,
      contractAddress,
      collectionName,
      enabled,
      tokenMetadataMap,
      collectionTokens
    };
  }, [marketplaceAddress, chainId, contractAddress, collectionName, enabled, tokenMetadataMap, collectionTokens]);

  // Stable fetch function using refs
  const fetchCollectionOffers = useCallback(async () => {
    const {
      marketplaceAddress: marketplaceAddress_0,
      chainId: chainId_0,
      contractAddress: contractAddress_0,
      collectionName: collectionName_0,
      enabled: enabled_0,
      tokenMetadataMap: tokenMetadataMap_0,
      collectionTokens: collectionTokens_0
    } = latestValuesRef.current;
    if (!marketplaceAddress_0 || !enabled_0 || !contractAddress_0) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      console.log(`🔍 Fetching offers for collection ${collectionName_0}...`);

      // Get total offers in marketplace
      const totalOffers = await readContract(config, {
        address: marketplaceAddress_0 as Address,
        abi: offersAbi,
        functionName: 'totalOffers',
        chainId: chainId_0
      });
      const total = Number(totalOffers || 0n);
      if (total === 0) {
        setNftsWithOffers([]);
        setLoading(false);
        return;
      }

      // Fetch all offers in batches
      const batchSize = 100;
      const batches = Math.ceil(total / batchSize);
      const collectionOffers: Offer[] = [];
      for (let i = 0; i < batches; i++) {
        const startId = BigInt(i * batchSize);
        const endId = BigInt(Math.min((i + 1) * batchSize - 1, total - 1));
        try {
          const batchOffers = await readContract(config, {
            address: marketplaceAddress_0 as Address,
            abi: offersAbi,
            functionName: 'getAllValidOffers',
            args: [startId, endId],
            chainId: chainId_0
          });
          if (batchOffers) {
            const offers = batchOffers as Offer[];

            // Filter offers for this specific collection
            const relevantOffers = offers.filter(offer => offer.assetContract.toLowerCase() === contractAddress_0.toLowerCase());
            collectionOffers.push(...relevantOffers);
          }
        } catch (batchError) {
          console.error(`❌ Error fetching offers batch ${i}:`, batchError);
        }
      }

      // Group offers by tokenId
      const offersByTokenId = new Map<string, Offer[]>();
      collectionOffers.forEach(offer_0 => {
        const tokenId = offer_0.tokenId.toString();
        if (!offersByTokenId.has(tokenId)) {
          offersByTokenId.set(tokenId, []);
        }
        offersByTokenId.get(tokenId)!.push(offer_0);
      });

      // Create NFTWithOffers array - only for NFTs the user owns
      const userOwnedTokenIds = new Set(collectionTokens_0.map(token_0 => token_0.tokenId.toString()));
      const nftsWithOffersArray: NFTWithOffers[] = [];
      offersByTokenId.forEach((offers_0, tokenId_0) => {
        // Only include if user owns this NFT
        if (userOwnedTokenIds.has(tokenId_0)) {
          const metadata = tokenMetadataMap_0.get(tokenId_0);
          nftsWithOffersArray.push({
            tokenId: tokenId_0,
            offers: offers_0,
            metadata
          });
        }
      });

      // Sort by highest offer value
      nftsWithOffersArray.sort((a, b) => {
        const aHighest = Math.max(...a.offers.map(o => Number(o.totalPrice)));
        const bHighest = Math.max(...b.offers.map(o_0 => Number(o_0.totalPrice)));
        return bHighest - aHighest;
      });
      setNftsWithOffers(nftsWithOffersArray);
      console.log(`✅ Found ${nftsWithOffersArray.length} NFTs with offers in ${collectionName_0}:`, {
        totalOffersInCollection: collectionOffers.length,
        nftsWithOffers: nftsWithOffersArray.length
      });
    } catch (err) {
      console.error('❌ Error fetching collection offers:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch collection offers'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch offers when enabled and dependencies are ready - with deduplication
  useEffect(() => {
    if (enabled && contractAddress && !tokensLoading && marketplaceAddress) {
      const fetchKey = `${contractAddress}-${collectionName}-${enabled}`;
      if (fetchKey === lastFetchKeyRef.current) {
        return; // Already fetched for this combination
      }
      console.log(`🔄 Collection offers effect triggered for ${collectionName}`);
      lastFetchKeyRef.current = fetchKey;

      // Add small delay to prevent rapid refetching
      const timeoutId = setTimeout(() => {
        fetchCollectionOffers();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [enabled, contractAddress, tokensLoading, marketplaceAddress, collectionName, fetchCollectionOffers]);
  const totalOffersCount = nftsWithOffers.reduce((sum, nft) => sum + nft.offers.length, 0);
  return {
    nftsWithOffers,
    loading: loading || tokensLoading,
    error,
    totalOffersCount,
    refetch: fetchCollectionOffers
  };
}